/**
 * Plan a reorganization — draft, map, work the list, authorize.
 *
 * The admin edits a PRIVATE copy of the structure; the live tree is untouched
 * until authorization. Every unit that leaves the draft needs a disposition
 * (merged into / split into / retired), and what those units still hold is
 * shown as a worklist:
 *
 *   standing references   move on their own for a merge; need a decision for
 *                         a split or a retirement
 *   in-flight work        stays with the retired unit — a cycle finishes under
 *                         the structure it started with (owner ruling D2)
 *   permission grants     never move on their own, even on a merge
 *
 * All writes are RPCs (mig 732); the server re-checks authority and validity.
 * Spec: docs/9-exploration/PRD-structure-versioning-and-transitions.md FR-2–5.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox, Select, TextArea, TextInput } from '../ds';
import type { HierarchyKind } from '../../lib/hierarchy/hierarchyTypes';
import { structureHistoryAnchorId } from '../../lib/hierarchy/structureRevisions';
import {
  DraftEditError,
  addDraftNode,
  departedNodes,
  draftProblems,
  moveDraftNode,
  orderDraftAsTree,
  pruneDispositions,
  removeDraftNode,
  renameDraftNode,
  restoreDraftNode,
  type DepartureDisposition,
  type DraftDisposition,
  type DraftNode,
} from '../../lib/hierarchy/structureDraft';
import {
  authorizeStructureRevision,
  createStructureDraft,
  discardStructureDraft,
  getOpenStructureDraft,
  getStructureDraftImpact,
  resolveStructureReferences,
  saveStructureDraft,
  type ImpactReference,
  type OpenStructureDraft,
  type StructureDraftImpact,
} from '../../lib/hierarchy/structureReorgService';

const LEVELS: Record<HierarchyKind, Array<{ value: string; label: string }>> = {
  org: [
    { value: 'organization', label: 'Organization' },
    { value: 'division', label: 'Division' },
    { value: 'department', label: 'Department' },
    { value: 'team', label: 'Team' },
    { value: 'workgroup', label: 'Workgroup' },
  ],
  product: [
    { value: 'portfolio', label: 'Portfolio' },
    { value: 'product_line', label: 'Product line' },
    { value: 'product', label: 'Product' },
    { value: 'component', label: 'Component' },
  ],
};

const DISPOSITION_LABEL: Record<DepartureDisposition, string> = {
  merged_into: 'Merged into another unit',
  split_into: 'Split into several units',
  retired: 'Retired — nothing replaces it',
};

const LEAVE = '__leave__';

interface StructureReorgWorkspaceProps {
  workspaceId: string | null | undefined;
  kind: HierarchyKind;
  /** Called after an authorization or a discard so the host can reload the live tree. */
  onLiveStructureChanged?: () => void;
}

export function StructureReorgWorkspace({ workspaceId, kind, onLiveStructureChanged }: StructureReorgWorkspaceProps) {
  const [draft, setDraft] = useState<OpenStructureDraft | null>(null);
  const [nodes, setNodes] = useState<DraftNode[]>([]);
  const [dispositions, setDispositions] = useState<DraftDisposition[]>([]);
  const [impact, setImpact] = useState<StructureDraftImpact | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Next step after authorizing: the history panel (it reloads to the newest
  // version) shows the new version against the one it replaced.
  const [authorizedVersion, setAuthorizedVersion] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [pendingTargets, setPendingTargets] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const open = await getOpenStructureDraft(workspaceId, kind);
      setDraft(open);
      setNodes(open?.nodes ?? []);
      setDispositions(open?.dispositions ?? []);
      setDirty(false);
      setImpact(open ? await getStructureDraftImpact(open.id) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const base = useMemo(() => draft?.base ?? [], [draft]);
  const tree = useMemo(() => orderDraftAsTree(nodes), [nodes]);
  const departed = useMemo(() => departedNodes(base, nodes), [base, nodes]);
  const baseIds = useMemo(() => new Set(base.map((n) => n.nodeId)), [base]);
  const baseById = useMemo(() => new Map(base.map((n) => [n.nodeId, n])), [base]);
  const problems = useMemo(
    () => draftProblems(base, nodes, dispositions, { singleRoot: kind === 'org' }),
    [base, nodes, dispositions, kind],
  );
  const selected = nodes.find((n) => n.nodeId === selectedId) ?? null;
  const nodeOptions = useMemo(() => tree.map((n) => ({ value: n.nodeId, label: `${'— '.repeat(n.depth)}${n.name}` })), [tree]);

  const edit = (fn: () => DraftNode[]) => {
    setError(null);
    setNotice(null);
    setAuthorizedVersion(null);
    try {
      const next = fn();
      setNodes(next);
      setDispositions((cur) => pruneDispositions(base, next, cur));
      setDirty(true);
    } catch (err) {
      setError(err instanceof DraftEditError ? err.message : String(err));
    }
  };

  const setDisposition = (fromNodeId: string, patch: Partial<DraftDisposition>) => {
    setDispositions((cur) => {
      const existing = cur.find((d) => d.fromNodeId === fromNodeId);
      const merged: DraftDisposition = { fromNodeId, disposition: 'retired', successorIds: [], ...existing, ...patch };
      if (merged.disposition === 'retired') merged.successorIds = [];
      if (merged.disposition === 'merged_into') merged.successorIds = merged.successorIds.slice(0, 1);
      return [...cur.filter((d) => d.fromNodeId !== fromNodeId), merged];
    });
    setDirty(true);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    setAuthorizedVersion(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<StructureDraftImpact | null> => {
    if (!draft) return null;
    const next = await saveStructureDraft(draft.id, nodes, dispositions);
    setImpact(next);
    setDirty(false);
    return next;
  };

  const handleStart = () =>
    run(async () => {
      if (!workspaceId) return;
      await createStructureDraft(workspaceId, kind);
      await load();
    });

  const handleDiscard = () => {
    if (!draft || !confirm('Discard this reorganization draft? The live structure is unchanged either way.')) return;
    void run(async () => {
      await discardStructureDraft(draft.id);
      await load();
    });
  };

  const handleResolve = (fromNodeId: string, ref: ImpactReference) =>
    run(async () => {
      if (!draft) return;
      if (dirty) await save();
      const target = pendingTargets[`${fromNodeId}:${ref.refType}`] ?? LEAVE;
      setImpact(await resolveStructureReferences(draft.id, ref.refType, ref.refIds, fromNodeId, target === LEAVE ? null : target));
    });

  const handleAuthorize = () =>
    run(async () => {
      if (!draft) return;
      const current = dirty ? await save() : impact;
      const unresolved = current?.unresolvedCount ?? 0;
      if (unresolved > 0 && !overrideReason.trim()) {
        throw new Error(`${unresolved} reference${unresolved === 1 ? ' is' : 's are'} still undecided. Decide them, or write an override reason.`);
      }
      if (!confirm('Authorize this reorganization? It changes the live structure for everyone in the workspace, as one new version.')) return;
      const result = await authorizeStructureRevision(draft.id, unresolved > 0 ? overrideReason : undefined);
      setAuthorizedVersion(result.version);
      setNotice(
        `${result.version} is now the current structure: ${result.added} added, ${result.changed} changed, ${result.retired} retired, ${result.referencesMoved} reference${result.referencesMoved === 1 ? '' : 's'} moved.`,
      );
      setOverrideReason('');
      await load();
      onLiveStructureChanged?.();
    });

  if (!workspaceId) return null;

  return (
    <section className="border border-[var(--rule)] bg-[var(--surface)] p-4" data-testid="structure-reorg">
      <div className="flex items-center gap-2 mb-1">
        <ArrowRightLeft className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Plan a reorganization</h3>
        {(loading || busy) && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)]" />}
      </div>
      <p className="text-xs text-[var(--text-sec)] mb-3">
        Work on a private draft. Nothing changes for anyone until you authorize it; then it becomes one new version of the
        structure, and every unit that left it is recorded as merged, split or retired.
      </p>

      {error && <p className="text-xs text-[var(--coral)] mb-2" data-testid="structure-reorg-error">{error}</p>}
      {notice && (
        <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mb-2" data-testid="structure-reorg-notice">
          {notice}
          {authorizedVersion && (
            <>
              {' '}
              <Button
                variant="ghost"
                size="sm"
                className="underline !px-1 !py-0 text-xs"
                data-testid="structure-reorg-review-link"
                onClick={() =>
                  document.getElementById(structureHistoryAnchorId(kind))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              >
                Review what changed in {authorizedVersion}
              </Button>
            </>
          )}
        </p>
      )}

      {!loading && !draft && (
        <Button onClick={handleStart} disabled={busy} variant="secondary" size="sm" className="gap-1 px-3 py-1.5 text-xs">
          <Plus className="h-3 w-3" /> Start a draft
        </Button>
      )}

      {draft && (
        <div className="space-y-5">
          {impact?.baseIsStale && (
            <p className="text-xs text-[var(--coral)]">
              The live structure changed after this draft was opened, so it can no longer be authorized. Discard it and start
              again from the current version.
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-4">
            <div>
              <h4 className="text-xs font-semibold text-[var(--text-pri)] mb-1">
                Draft of v{draft.major}.0{dirty ? ' · unsaved changes' : ''}
              </h4>
              <ul className="max-h-80 overflow-y-auto text-sm border border-[var(--rule)]" data-testid="structure-reorg-tree">
                {tree.map((n) => {
                  const original = baseById.get(n.nodeId);
                  const tag = !baseIds.has(n.nodeId)
                    ? 'New'
                    : original && original.name !== n.name && original.parentNodeId !== n.parentNodeId
                      ? 'Renamed and moved'
                      : original && original.name !== n.name
                        ? 'Renamed'
                        : original && original.parentNodeId !== n.parentNodeId
                          ? 'Moved'
                          : null;
                  return (
                    <li key={n.nodeId}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedId(n.nodeId);
                          setNewName('');
                        }}
                        className={`w-full text-left flex items-center gap-2 py-1 pr-2 ${
                          n.nodeId === selectedId ? 'bg-[var(--slate-dim)]' : 'hover:bg-[var(--hover-tint)]'
                        }`}
                        style={{ paddingLeft: `${8 + n.depth * 16}px` }}
                      >
                        <span className="text-[var(--text-pri)] truncate">{n.name}</span>
                        {n.level && <span className="text-xs text-[var(--text-ter)]">{n.level.replace(/_/g, ' ')}</span>}
                        {tag && <span className="text-xs px-1.5 bg-[var(--slate-dim)] text-[var(--text-sec)]">{tag}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="text-xs space-y-3">
              {!selected ? (
                <p className="text-[var(--text-ter)]">Select a unit to rename it, move it, add a unit under it, or remove it.</p>
              ) : (
                <>
                  <h4 className="font-semibold text-[var(--text-pri)]">{selected.name}</h4>
                  <label className="block">
                    <span className="text-[var(--text-sec)]">Name</span>
                    <TextInput
                      size="sm"
                      className="mt-1 w-full"
                      defaultValue={selected.name}
                      key={`${selected.nodeId}:${selected.name}`}
                      onBlur={(e) => {
                        if (e.target.value.trim() !== selected.name) edit(() => renameDraftNode(nodes, selected.nodeId, e.target.value));
                      }}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[var(--text-sec)]">Sits under</span>
                    <Select
                      size="sm"
                      className="mt-1 w-full"
                      value={selected.parentNodeId ?? ''}
                      onChange={(e) => edit(() => moveDraftNode(nodes, selected.nodeId, e.target.value || null))}
                    >
                      <option value="">{kind === 'org' ? '(top of the chart)' : '(top level)'}</option>
                      {nodeOptions
                        .filter((o) => o.value !== selected.nodeId)
                        .map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                    </Select>
                  </label>
                  <div className="flex items-end gap-2">
                    <label className="block flex-1">
                      <span className="text-[var(--text-sec)]">Add a unit under it</span>
                      <TextInput
                        size="sm"
                        className="mt-1 w-full"
                        value={newName}
                        placeholder="Name of the new unit"
                        onChange={(e) => setNewName(e.target.value)}
                      />
                    </label>
                    <Button
                      variant="secondary" size="sm" className="gap-1 px-3 py-1.5 text-xs"
                      disabled={!newName.trim()}
                      onClick={() => {
                        const levels = LEVELS[kind];
                        const idx = Math.min(levels.findIndex((l) => l.value === selected.level) + 1, levels.length - 1);
                        edit(() =>
                          addDraftNode(nodes, {
                            nodeId: crypto.randomUUID(),
                            parentNodeId: selected.nodeId,
                            name: newName,
                            level: levels[Math.max(idx, 0)].value,
                          }),
                        );
                        setNewName('');
                      }}
                    >
                      <Plus className="h-3 w-3" /> Add
                    </Button>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    className="inline-flex items-center !gap-1 !px-3 !py-1.5 !text-xs border border-[var(--coral)] text-[var(--coral)] hover:bg-[var(--coral-dim)]"
                    onClick={() => {
                      edit(() => removeDraftNode(nodes, selected.nodeId));
                      setSelectedId(null);
                    }}
                  >
                    <Trash2 className="h-3 w-3" /> Remove from the new structure
                  </Button>
                </>
              )}
            </div>
          </div>

          {departed.length > 0 && (
            <div data-testid="structure-reorg-departed">
              <h4 className="text-xs font-semibold text-[var(--text-pri)] mb-1">Units leaving the structure</h4>
              <p className="text-xs text-[var(--text-sec)] mb-2">
                Say what becomes of each one. They are retired, never deleted, so past decisions made under them stay readable.
              </p>
              <ul className="space-y-3">
                {departed.map((n) => {
                  const d = dispositions.find((x) => x.fromNodeId === n.nodeId);
                  const report = impact?.departed.find((x) => x.nodeId === n.nodeId);
                  return (
                    <li key={n.nodeId} className="border border-[var(--rule)] p-3 text-xs">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-[var(--text-pri)]">{n.name}</span>
                        <Button
                          variant="ghost" size="sm" className="!gap-1 !px-2 !py-1 !text-xs"
                          onClick={() => edit(() => restoreDraftNode(nodes, base, n.nodeId))}
                        >
                          <RotateCcw className="h-3 w-3" /> Put it back
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Select
                          size="sm"
                          value={d?.disposition ?? ''}
                          onChange={(e) => setDisposition(n.nodeId, { disposition: e.target.value as DepartureDisposition })}
                        >
                          <option value="" disabled>
                            What becomes of it?
                          </option>
                          {(Object.keys(DISPOSITION_LABEL) as DepartureDisposition[]).map((k) => (
                            <option key={k} value={k}>
                              {DISPOSITION_LABEL[k]}
                            </option>
                          ))}
                        </Select>
                        {d?.disposition === 'merged_into' && (
                          <Select
                            size="sm"
                            value={d.successorIds[0] ?? ''}
                            onChange={(e) => setDisposition(n.nodeId, { successorIds: e.target.value ? [e.target.value] : [] })}
                          >
                            <option value="">Merged into…</option>
                            {nodeOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Select>
                        )}
                      </div>
                      {d?.disposition === 'split_into' && (
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          {tree.map((o) => (
                            <Checkbox
                              key={o.nodeId}
                              label={o.name}
                              checked={d.successorIds.includes(o.nodeId)}
                              onChange={(e) =>
                                setDisposition(n.nodeId, {
                                  successorIds: e.target.checked
                                    ? [...d.successorIds, o.nodeId]
                                    : d.successorIds.filter((id) => id !== o.nodeId),
                                })
                              }
                            />
                          ))}
                        </div>
                      )}

                      {report && report.references.length > 0 && (
                        <ul className="mt-3 space-y-1.5" data-testid="structure-reorg-worklist">
                          {report.references.map((ref) => {
                            const key = `${n.nodeId}:${ref.refType}`;
                            const successorName = nodes.find((x) => x.nodeId === report.successorIds[0])?.name;
                            if (ref.klass === 'in_flight') {
                              return (
                                <li key={ref.refType} className="text-[var(--text-sec)]">
                                  {ref.label}: {ref.count} — stays with this unit. Work in flight finishes under the structure it
                                  started with.
                                </li>
                              );
                            }
                            if (ref.autoCarry) {
                              return (
                                <li key={ref.refType} className="text-[var(--text-sec)]">
                                  {ref.label}: {ref.count} — moves to {successorName ?? 'the unit it merges into'} automatically.
                                </li>
                              );
                            }
                            const done = ref.resolved >= ref.count;
                            return (
                              <li key={ref.refType} className="flex flex-wrap items-center gap-2">
                                <span className={done ? 'text-[var(--text-sec)]' : 'text-[var(--text-pri)] font-semibold'}>
                                  {ref.label}: {ref.count}
                                  {ref.klass === 'authority' ? ' — permissions never move on their own' : ''}
                                  {done ? ' — decided' : ' — needs a decision'}
                                </span>
                                <Select
                                  size="sm"
                                  value={pendingTargets[key] ?? LEAVE}
                                  onChange={(e) => setPendingTargets((cur) => ({ ...cur, [key]: e.target.value }))}
                                >
                                  <option value={LEAVE}>Leave on the retired unit</option>
                                  {nodeOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      Move to {o.label}
                                    </option>
                                  ))}
                                </Select>
                                <Button
                                  variant="secondary" size="sm" className="px-2 py-1 text-xs"
                                  disabled={busy || !d}
                                  onClick={() => void handleResolve(n.nodeId, ref)}
                                >
                                  {done ? 'Change' : 'Resolve'}
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {dirty && <p className="mt-2 text-[var(--text-ter)]">Save the draft to see what this unit still holds.</p>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {impact && impact.depthChanges.some((c) => c.grants > 0) && (
            <div className="text-xs text-[var(--text-sec)]">
              <h4 className="font-semibold text-[var(--text-pri)] mb-1">Permission grants whose reach changes</h4>
              {impact.depthChanges
                .filter((c) => c.grants > 0)
                .map((c) => (
                  <p key={c.nodeId}>
                    {c.name} moves from level {c.fromDepth} to level {c.toDepth} and carries {c.grants} role grant
                    {c.grants === 1 ? '' : 's'} — review them after authorizing.
                  </p>
                ))}
            </div>
          )}

          {problems.length > 0 && (
            <ul className="text-xs text-[var(--coral)] space-y-0.5" data-testid="structure-reorg-problems">
              {problems.map((p, i) => (
                <li key={`${p.nodeId ?? 'draft'}:${i}`}>{p.message}</li>
              ))}
            </ul>
          )}

          {(impact?.unresolvedCount ?? 0) > 0 && (
            <label className="block text-xs">
              <span className="text-[var(--text-sec)]">
                {impact?.unresolvedCount} reference{impact?.unresolvedCount === 1 ? ' is' : 's are'} undecided. To authorize anyway,
                say why — it is recorded on the version.
              </span>
              <TextArea
                className="mt-1 w-full"
                rows={2}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </label>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary" size="sm" className="px-3 py-1.5 text-xs"
              disabled={busy || !dirty}
              onClick={() => void run(async () => void (await save()))}
            >
              Save draft
            </Button>
            <Button
              variant="primary" size="sm" className="px-3 py-1.5 text-xs"
              disabled={busy || problems.length > 0 || Boolean(impact?.baseIsStale)}
              onClick={() => void handleAuthorize()}
            >
              Authorize as v{draft.major}.0
            </Button>
            <Button variant="ghost" size="sm" className="px-3 py-1.5 text-xs" disabled={busy} onClick={handleDiscard}>
              Discard draft
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
